import React, { useState, useCallback, useRef } from "react";
import {
  Box, Typography, LinearProgress, IconButton, Alert, Chip, Button,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import axios from "axios";
import api from "../utils/api";

const MAX_FILES     = 10;
const MAX_SIZE_MB   = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ACCEPTED_EXT   = ".jpg, .jpeg, .png, .webp";

// ─── FileUploader ─────────────────────────────────────────────────────────────
const FileUploader = React.memo(({ setImgServicio, imgServicio, setLoadFile, idInterno }) => {
  const [previews, setPreviews]   = useState([]); // [{ file, url }]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [uploaded, setUploaded]   = useState(false);
  const [error, setError]         = useState("");
  const [dragOver, setDragOver]   = useState(false);
  const inputRef = useRef(null);
  const userToken = localStorage.getItem("user-token");

  // ── Agregar archivos (desde input o drop) ──────────────────────────────────
  const addFiles = useCallback((fileList) => {
    setError("");
    setUploaded(false);
    const incoming = Array.from(fileList);
    const errs = [];
    const valid = [];

    for (const file of incoming) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        errs.push(`"${file.name}" no es una imagen válida`);
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        errs.push(`"${file.name}" supera los ${MAX_SIZE_MB} MB`);
        continue;
      }
      valid.push({ file, url: URL.createObjectURL(file) });
    }

    if (errs.length) setError(errs.join(" · "));

    setPreviews(prev => {
      // Deduplicar por nombre + tamaño
      const existing = new Set(prev.map(p => `${p.file.name}-${p.file.size}`));
      const news = valid.filter(v => !existing.has(`${v.file.name}-${v.file.size}`));
      const next = [...prev, ...news].slice(0, MAX_FILES);
      setLoadFile(next.length > 0);
      return next;
    });
  }, [setLoadFile]);

  const removeFile = useCallback((idx) => {
    setPreviews(prev => {
      URL.revokeObjectURL(prev[idx]?.url);
      const next = prev.filter((_, i) => i !== idx);
      setLoadFile(next.length > 0);
      if (next.length === 0) {
        setUploaded(false);
        setImgServicio([]);
      }
      return next;
    });
  }, [setLoadFile, setImgServicio]);

  // ── Drag & drop handlers ───────────────────────────────────────────────────
  const onDragOver  = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const onDragLeave = useCallback(() => setDragOver(false), []);
  const onDrop      = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (previews.length === 0 || uploading) return;
    setUploading(true);
    setProgress(0);
    setError("");

    try {
      const formData = new FormData();
      previews.forEach(({ file }) => formData.append("images", file));

      const response = await axios.post(api.urlAPI + "files", formData, {
        headers: { "iron-token": userToken },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });

      setImgServicio(response.data?.files || []);
      setUploaded(true);
      setLoadFile(false);
      setProgress(100);
    } catch (err) {
      const serverMsg = err?.response?.data?.error || err?.message || "";
      setError(`No se pudieron subir las imágenes. ${serverMsg || "Verificá tu conexión e intentá de nuevo."}`);
      setLoadFile(previews.length > 0);
    } finally {
      setUploading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const hasFiles = previews.length > 0;

  return (
    <Box>
      {/* Label */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
        <ImageOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Fotos del servicio
        </Typography>
        {hasFiles && (
          <Chip
            label={`${previews.length} / ${MAX_FILES}`}
            size="small"
            sx={{ height: 18, fontSize: 10, ml: "auto" }}
          />
        )}
      </Box>

      {/* Drop zone (visible cuando no hay archivos o para agregar más) */}
      {!uploaded && (
        <Box
          onClick={() => inputRef.current?.click()}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          sx={{
            border: "1.5px dashed",
            borderColor: dragOver ? "primary.main" : "divider",
            borderRadius: 2,
            p: hasFiles ? 1 : 3,
            display: "flex",
            flexDirection: hasFiles ? "row" : "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            cursor: "pointer",
            bgcolor: dragOver ? "action.hover" : "action.disabledBackground",
            transition: "all 0.15s ease",
            "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
          }}
        >
          <CloudUploadOutlinedIcon
            sx={{ fontSize: hasFiles ? 18 : 32, color: dragOver ? "primary.main" : "text.disabled", flexShrink: 0 }}
          />
          {hasFiles ? (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Agregar más fotos
            </Typography>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                Tocá o arrastrá fotos aquí
              </Typography>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                {ACCEPTED_EXT} · máx. {MAX_SIZE_MB} MB c/u · hasta {MAX_FILES} fotos
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXT}
        multiple
        style={{ display: "none" }}
        onChange={(e) => addFiles(e.target.files)}
      />

      {/* Grid de thumbnails */}
      {hasFiles && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
            gap: 0.8,
            mt: 1,
          }}
        >
          {previews.map(({ url, file }, idx) => (
            <Box
              key={idx}
              sx={{
                position: "relative",
                borderRadius: 1.5,
                overflow: "hidden",
                aspectRatio: "1",
                bgcolor: "action.disabledBackground",
                border: "1px solid",
                borderColor: uploaded ? "success.dark" : "divider",
              }}
            >
              <img
                src={url}
                alt={file.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              {/* Checkmark overlay cuando está subida */}
              {uploaded && (
                <Box sx={{
                  position: "absolute", inset: 0,
                  bgcolor: "rgba(0,0,0,0.35)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <CheckCircleOutlineIcon sx={{ color: "#4ade80", fontSize: 22 }} />
                </Box>
              )}
              {/* Botón eliminar (solo si no está subiendo) */}
              {!uploading && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                  sx={{
                    position: "absolute", top: 2, right: 2,
                    width: 20, height: 20,
                    bgcolor: "rgba(0,0,0,0.65)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.85)" },
                    p: 0,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Barra de progreso */}
      {uploading && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Subiendo fotos…</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>{progress}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ borderRadius: 1, height: 4 }}
          />
        </Box>
      )}

      {/* Botón de upload */}
      {hasFiles && !uploaded && (
        <Button
          fullWidth
          variant="contained"
          onClick={handleUpload}
          disabled={uploading || previews.length === 0}
          sx={{
            mt: 1.5,
            bgcolor: "#ED9519",
            color: "#1a0e00",
            fontWeight: 600,
            borderRadius: 2,
            "&:hover": { bgcolor: "#d4851a" },
            "&.Mui-disabled": { bgcolor: "rgba(237,149,25,0.3)", color: "rgba(26,14,0,0.5)" },
          }}
        >
          {uploading ? `Subiendo… ${progress}%` : `Subir ${previews.length} foto${previews.length !== 1 ? "s" : ""}`}
        </Button>
      )}

      {/* Estado: subido OK */}
      {uploaded && (
        <Box sx={{
          mt: 1.5, display: "flex", alignItems: "center", gap: 1,
          p: 1, borderRadius: 2,
          bgcolor: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.25)",
        }}>
          <CheckCircleOutlineIcon sx={{ color: "#4ade80", fontSize: 18 }} />
          <Typography variant="caption" sx={{ color: "#4ade80", fontWeight: 500 }}>
            {previews.length} foto{previews.length !== 1 ? "s" : ""} subida{previews.length !== 1 ? "s" : ""} correctamente
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mt: 1.5, fontSize: 12, py: 0.5 }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
});

export default FileUploader;
